import {
  normalizePositionIdentity,
  PositionWithIdentity,
} from "../../utils/positionIdentity";

/**
 * What we knew about a position at the moment the trader acted on it.
 *
 * There is NO server-issued id on the positions payload
 * (`GetAccount200ResponsePositionsInner` has no `id` / `position_key` field), so
 * a position is identified by a composite over fields that are partly optional.
 * `amount` and `avgEntryPrice` are recorded so a later row can be *falsified* —
 * see `hasDiverged`.
 */
export interface PositionFingerprint {
  /** Present only on raw indexer patches and position-history rows. */
  positionKey?: string;
  instrumentId?: string;
  instrumentName?: string;
  side?: string;
  marginType?: string;
  amount?: string;
  avgEntryPrice?: string;
}

export const toFingerprint = (
  position: PositionWithIdentity
): PositionFingerprint => {
  const normalized = normalizePositionIdentity(position) || {};
  return {
    positionKey: normalized.position_key || normalized.positionKey,
    instrumentId: normalized.instrument_id,
    instrumentName: normalized.instrument_name,
    side: normalized.side,
    marginType: normalized.margin_type,
    amount: position.amount,
    avgEntryPrice: position.avg_entry_price,
  };
};

/**
 * Could this server row BE the fingerprinted position?
 *
 * "Could", not "is". A field narrows the match only when BOTH sides carry it —
 * comparing a known value against a missing one proves nothing, so it must not
 * exclude. Being permissive here is the safe direction: callers only ever act
 * on a UNIQUE match, so a looser bind produces ambiguity (and therefore
 * inaction), never a confident wrong answer.
 */
export const canBind = (
  fingerprint: PositionFingerprint,
  row: PositionWithIdentity
): boolean => {
  const normalized = normalizePositionIdentity(row) || {};
  const rowKey = normalized.position_key || normalized.positionKey;

  // A server-issued key is exact and terminal.
  if (fingerprint.positionKey && rowKey) {
    return fingerprint.positionKey === rowKey;
  }

  // Compare on whichever instrument dimension both sides actually carry.
  //
  // `instrument_id` is an Ethereum address, and the two sources disagree about
  // case: `/account` can return it checksummed while a raw indexer patch sends
  // it lowercase. Comparing those with `!==` made every bind fail, so the close
  // neither hid the row NOR released — leaving an entry that nothing could
  // clear. `sameMarket` in usePerpOrder.helpers already lowercases both sides;
  // this now agrees with it.
  if (fingerprint.instrumentId && normalized.instrument_id) {
    if (
      fingerprint.instrumentId.toLowerCase() !==
      normalized.instrument_id.toLowerCase()
    ) {
      return false;
    }
  } else if (fingerprint.instrumentName && normalized.instrument_name) {
    if (fingerprint.instrumentName !== normalized.instrument_name) return false;
  } else {
    // No shared instrument dimension — nothing ties these together.
    return false;
  }

  if (fingerprint.side && normalized.side && fingerprint.side !== normalized.side) {
    return false;
  }

  if (
    fingerprint.marginType &&
    normalized.margin_type &&
    fingerprint.marginType !== normalized.margin_type
  ) {
    return false;
  }

  return true;
};

/** Indices of every row this fingerprint could refer to. */
export const findBindableRows = (
  fingerprint: PositionFingerprint,
  rows: readonly PositionWithIdentity[]
): number[] =>
  rows.reduce<number[]>((acc, row, index) => {
    if (canBind(fingerprint, row)) acc.push(index);
    return acc;
  }, []);

/**
 * Do two recorded decimal fields differ in VALUE?
 *
 * Compared numerically, not as strings, because the two sources that feed this
 * module format numbers differently. `usePositionsWSS` renormalises a merged
 * websocket patch with `String(Math.abs(Number(amount)))`, while `/account`
 * rows are passed through verbatim — so the SAME position can be recorded as
 * `"0.010"` in one place and `"0.01"` in the other (likewise `"1.0"`/`"1"`,
 * `"10.50"`/`"10.5"`).
 *
 * String equality read that as a divergence, which RELEASED the pending close
 * and put the just-closed row back on screen — precisely the behaviour this
 * module exists to prevent. Live Arc payloads happen to use canonical decimals,
 * so it stayed latent, but nothing guarantees that.
 *
 * Absent values never differ (a field only narrows when both sides carry it),
 * and anything that is not a finite number on both sides falls back to string
 * comparison rather than guessing.
 */
const differsInValue = (
  recorded: string | undefined,
  observed: string | undefined
): boolean => {
  if (recorded === undefined || observed === undefined) return false;
  if (recorded === observed) return false;

  const a = Number(recorded);
  const b = Number(observed);
  // `Number("")` is 0, so empty strings must not be treated as numeric.
  const numeric =
    recorded.trim() !== "" &&
    observed.trim() !== "" &&
    Number.isFinite(a) &&
    Number.isFinite(b);

  // Not both numeric: they are unequal strings, so report the difference.
  return numeric ? a !== b : true;
};

/**
 * Is this row provably NOT the position we acted on?
 *
 * A completed full close cannot mutate the row it closed — the row is either
 * unchanged or gone. So a bindable row whose size or entry price differs from
 * what we recorded is a different position: most often the trader re-opened on
 * the same market and side. This is one-way evidence: divergence proves "not
 * ours", but equality proves nothing at all and must never be read as staleness.
 */
export const hasDiverged = (
  fingerprint: PositionFingerprint,
  row: PositionWithIdentity
): boolean => {
  if (differsInValue(fingerprint.amount, row.amount)) return true;
  if (differsInValue(fingerprint.avgEntryPrice, row.avg_entry_price)) {
    return true;
  }
  return false;
};

export type HideVerdict =
  /** Provably the position we closed, and provably the only candidate. */
  | { kind: "hide"; index: number }
  /** Bindable rows exist but we cannot tell which is ours — show them all. */
  | { kind: "ambiguous"; indices: number[] }
  /** A bindable row is provably a different position — release the pending close. */
  | { kind: "diverged"; index: number }
  /** Nothing to hide. NOT proof the close was indexed; simply a no-op. */
  | { kind: "no-match" };

/**
 * Decide what may be hidden for one pending close against one list of rows.
 *
 * `listComplete` is whether every row that could bind is loaded. Uniqueness is
 * a property of the whole candidate set, so with pages still unloaded a single
 * visible match is not provably unique — unless the bind was exact
 * (`positionKey`, or a full instrument+side+marginType tuple, which the router
 * treats as one position by construction).
 */
export const decideHide = (
  fingerprint: PositionFingerprint,
  rows: readonly PositionWithIdentity[],
  listComplete: boolean
): HideVerdict => {
  const matches = findBindableRows(fingerprint, rows);
  if (matches.length === 0) return { kind: "no-match" };

  // Falsification runs before any hiding.
  const divergedIndex = matches.find((index) =>
    hasDiverged(fingerprint, rows[index])
  );
  if (divergedIndex !== undefined) {
    return { kind: "diverged", index: divergedIndex };
  }

  if (matches.length > 1) return { kind: "ambiguous", indices: matches };

  const index = matches[0];
  const normalized = normalizePositionIdentity(rows[index]) || {};
  // `side` must be VERIFIED, not merely present on one side.
  //
  // `canBind` skips the side comparison whenever either side lacks it, so a
  // match can be "unique" without side ever having been compared. This flag is
  // what overrides `listComplete === false`, so treating margin type alone as
  // exact hid a row on the strength of instrument + margin type — and a raw
  // indexer close patch carries no `isLong`, no `amount` and no
  // `avg_entry_price`, which also leaves `hasDiverged` nothing to falsify with.
  //
  // Concretely: a liquidation of a CROSS SHORT could hide the trader's CROSS
  // LONG on the same market while later pages were still unloaded, and nothing
  // could release it. Requiring both sides to carry `side` closes that.
  const boundExactly = Boolean(
    (fingerprint.positionKey &&
      (normalized.position_key || normalized.positionKey)) ||
      (fingerprint.marginType &&
        normalized.margin_type &&
        fingerprint.side &&
        normalized.side)
  );

  if (boundExactly || listComplete) return { kind: "hide", index };
  return { kind: "ambiguous", indices: matches };
};
